import { FileExcelFilled } from "@ant-design/icons";
import { useApolloClient, useQuery } from "@apollo/client";
import { Button, Col, Empty, Form, Row, Table, message } from "antd";
import Search from "antd/lib/input/Search";
import { addDays, format, subDays } from "date-fns";
import React, { useEffect, useState } from "react";
import { Link, useMatch, useParams } from "react-router-dom";
import { PdfExportIcon } from "../assets/svg_icons";
import Error from "../components/apollo_error";
import DateRange from "../components/date_range";
import HorizontalScrollContainer from "../components/horizontal_scroll_container";
import LoadingSpinner from "../components/loading_indicator";
import MultiSelect from "../components/multi_select";
import { export_orders, getExportOrderVar } from "../graphql/export";
import {
  getOrdersVar2,
  get_orders2,
  get_total_order_count,
} from "../graphql/order";
import { ExportExcel } from "../helpers/excel";
import { formatMoney, getCurrency } from "../helpers/utils";
import withClinic from "../hooks/with_clinic";
import paths from "../routes/paths";
import {
  ApolloResult,
  Clinic,
  Order,
  OrderExcel,
  OrderExcelCheckIn,
  OrderItemExcel,
} from "../store";

import useInvoiceTemplate from "../helpers/invoiceHelper";
import { PaginationConfig } from "antd/lib/pagination";
import { Typography } from "antd";
interface P {
  currentClinic?: Clinic;
}

const SalePage = (props: P) => {
  const clinicId = props.currentClinic?.id || "";
  const isSellerSales = useMatch(paths.seller_sales);
  const { id: sellerId } = useParams() as { id?: string };

  const client = useApolloClient();
  const [searchText, setSearchText] = useState<string | null>(null);
  const [filter, setFilter] = useState<string[]>([]);
  const [paymentFilter, SetPaymentFilter] = useState<string[]>([]);
  const [pagination, setPagination] = useState<PaginationConfig | null>(null);
  const [exporting, setExporting] = useState(false);
  const [ranges, setRanges] = useState({
    startDate: subDays(new Date(), 15),
    endDate: addDays(new Date(), 15),
  });
  const [form] = Form.useForm();
  const take = pagination?.pageSize || 20;
  const skip = ((pagination?.current || 1) - 1) * take;
  const variables = getOrdersVar2({
    clinicId,
    startDate: ranges.startDate,
    endDate: ranges.endDate,
    searchText,
    take,
    skip,
    status: filter,
    payment_status: paymentFilter,
    ...(isSellerSales && {
      sellerId: sellerId,
    }),
  });
  const { loading, data, error } = useQuery<ApolloResult<"orders", Order[]>>(
    get_orders2,
    { variables }
  );
  const totalCount = useQuery<
    ApolloResult<"aggregateOrder", { _count: { id: number } }>
  >(get_total_order_count, { variables });

  const { template, loading: invoiceLoading } = useInvoiceTemplate(
    props.currentClinic?.code || ""
  );

  if (loading || invoiceLoading) return <LoadingSpinner />;
  if (error) return <Error error={error} />;
  if (!data || !template) return <Empty />;
  const onDateChange = (startDate: Date, endDate: Date) => {
    setRanges({ startDate, endDate });
  };
  const handleExport = async () => {
    if (!clinicId) {
      message.error("Please select a clinic before exporting.");
      return;
    }
    const fileName =
      "orders_" +
      format(ranges.startDate, "yyyy-MMM-dd") +
      "_" +
      format(ranges.endDate, "yyyy-MMM-dd");
    let excelData: any[] = [];
    const header = [
      "Order Date",
      "Order No",
      "Member",
      "Status",
      "Item Name",
      "Type",
      "Category",
      "Practitioner",
      "Item Quantity",
      "Item Price",
      "Net Amount",
      "Total Amount",
      "Payment Made",
      "Balance Due",
      "Order Discount",
      "Item Discount",
      "Item Markup",
      "Tax",
      "Payment Status",
      "Payment Type",
      "Split Payment",
      "Phone Number",
      "Seller",
      "Sold By",
      "Note",
    ];

    let list: OrderExcel[] = [];
    let rolling = true;
    const take = 500;
    let skip = 0;
    setExporting(true);
    try {
      while (rolling) {
        const variables = getExportOrderVar({
          clinicId,
          take,
          skip,
          startDate: ranges.startDate,
          endDate: ranges.endDate,
        });
        const result = await client.query<ApolloResult<"orders", OrderExcel[]>>({
          query: export_orders,
          variables,
        });
        const _orders = result.data?.orders || [];
        list = list.concat(_orders);
        skip += _orders.length;
        rolling = _orders.length === take;
      }
      for (const o of list) {
      let paymentIdx = 0;
      const practitionerUsage = new Map<string, number>();
      const isSplitPayment = o.payment_method === "SPLIT";
      const orderNo = o.order_id;
      const orderDate = format(new Date(o.created_at), "yyyy-MMM-dd");

      const discount = Number(o.discount);
      const tax = Number(o.tax);
      const totalAmount = Number(o.net_total);
      const payment =
        isSplitPayment && o?.payments != null
          ? o.payments[paymentIdx].payment_method
          : o.payment_method;
      const member =
        o.member.clinic_members.length > 0
          ? o.member.clinic_members[0].name
          : o.member.name;
      const phone = o.member.phonenumber;
      const soldBy = o.user.name;
      const splitPaymentAmount =
        isSplitPayment && o?.payments != null
          ? Number(o.payments[paymentIdx].payment_amount)
          : null;
      const paymentMade = getPaymentMadeAmount(o);
      const balanceDue = Number(o.credit_balance);
      const paymentSuatus = o.payment_status;
      const seller = o.seller?.display_name;
      const status = o.status;
      const merchantNote = o.metadata
        ? JSON.parse(o.metadata).merchant_note
        : "";

      for (const [i, item] of o.order_items.entries()) {
        const itemName = getOrderItemName(item);
        const type = getOrderItemType(item);
        const category = getOrderItemCategory(item);
        const practitioner = getOrderItemPractitioner(o, item, practitionerUsage);
        const quantity = Number(item.quantity);
        const price = Number(item.price);
        const netAmount = Number(item.total);
        const itemAdjustments = getOrderItemAdjustments(item);
        const payments = o?.payments;
        const paymentAmount =
          isSplitPayment && payments != null && paymentIdx < payments.length
            ? Number(payments[paymentIdx].payment_amount)
            : null;
        const paymentMethod =
          isSplitPayment && payments != null && paymentIdx < payments.length
            ? payments[paymentIdx].payment_method
            : "";
        excelData.push({
          orderDate,
          orderNo,
          member,
          status,
          itemName,
          type,
          category,
          practitioner,
          quantity,
          price,
          netAmount,
          totalAmount:
            i === 0
              ? isSplitPayment
                ? splitPaymentAmount
                : totalAmount
              : isSplitPayment
                ? paymentAmount
                : null,
          paymentMade: i === 0 ? paymentMade : null,
          balanceDue: i === 0 ? balanceDue : null,
          orderDiscount: i === 0 ? discount : null,
          itemDiscount: itemAdjustments.discount,
          itemMarkup: itemAdjustments.markup,
          tax: i === 0 ? tax : null,
          paymentSuatus: i === 0 ? paymentSuatus : "",
          payment: isSplitPayment
            ? i === 0
              ? payment
              : paymentMethod
            : o.payment_method,
          isSplit: isSplitPayment ? "SPLIT" : "",
          phone,
          seller,
          soldBy,
          merchantNote,
        });
        paymentIdx++;
      }

      if (
        isSplitPayment &&
        o?.payments != null &&
        paymentIdx < o.payments.length
      ) {
        for (const [i, payment] of o?.payments.entries()) {
          if (i < paymentIdx) continue;
          const paymentAmount = Number(payment.payment_amount);
          const paymentMethod = payment.payment_method;
          excelData.push({
            orderDate,
            orderNo,
            member,
            status,
            itemName: "",
            type: "",
            category: "",
            practitioner: "",
            quantity: null,
            price: null,
            netAmount: null,
            totalAmount: paymentAmount,
            paymentMade: null,
            balanceDue: null,
            orderDiscount: null,
            itemDiscount: null,
            markup: null,
            tax: null,
            paymentSuatus: "",
            payment: paymentMethod,
            isSplit: "SPLIT",
            phone,
            seller,
            soldBy,
            merchantNote,
          });
          paymentIdx++;
        }
      }
      }
      ExportExcel(excelData, fileName, header);
    } catch (error: any) {
      const gqlMessage =
        error?.graphQLErrors?.[0]?.message ||
        error?.networkError?.result?.errors?.[0]?.message ||
        error?.message ||
        "Export failed. Please try again.";
      message.error(gqlMessage);
      console.error("Sales export failed", {
        error,
        clinicId,
        startDate: ranges.startDate,
        endDate: ranges.endDate,
      });
    } finally {
      setExporting(false);
    }
  };

  const getOrderItemAdjustments = (
    item: OrderItemExcel
  ): { discount: number | null; markup: number | null } => {
    if (!item.metadata) return { discount: null, markup: null };
    try {
      const metadata = JSON.parse(item.metadata);
      const discount = Number(metadata?.discount ?? 0);
      const markup = Number(metadata?.markup ?? 0);
      return {
        discount:
          Number.isFinite(discount) && discount !== 0
            ? Math.abs(discount)
            : null,
        markup:
          Number.isFinite(markup) && markup !== 0 ? Math.abs(markup) : null,
      };
    } catch {
      return { discount: null, markup: null };
    }
  };

  const getOrderItemName = (item: OrderItemExcel) => {
    const metadata = JSON.parse(item.metadata);
    if (metadata?.name) {
      return metadata.name;
    }
    if (item.product_stock_item) {
      return item.product_stock_item.name;
    }
    if (item.member_service_package) {
      return item.member_service_package.service_package.name;
    }
    if (item.service) {
      return item.service.name;
    }
    if (item.service_package) {
      return item.service_package.name;
    }
    return "";
  };

  const getOrderItemType = (item: OrderItemExcel) => {
    if (item.product_stock_item) {
      return "product";
    }
    if (item.service) {
      return item.service.service_types?.[0]?.name || "service";
    }
    return "";
  };

  const getOrderItemCategory = (item: OrderItemExcel) => {
    const productStockItem = item.product_stock_item as
      | {
          product?: {
            product_categories?: Array<{ name: string }>;
          };
        }
      | undefined;
    if (productStockItem?.product?.product_categories?.length) {
      return productStockItem.product.product_categories[0].name;
    }
    const service = item.service as
      | {
          service_types?: Array<{
            service_type_category?: { name: string };
          }>;
        }
      | undefined;
    if (service?.service_types?.length) {
      return service.service_types[0].service_type_category?.name || "";
    }
    return "";
  };

  const getPaymentMadeAmount = (order: {
    payment_status?: string | null;
    payments?: { payment_amount: string | number }[] | null;
    balance: string | number;
    net_total: string | number;
  }): number => {
    const status = order.payment_status?.toUpperCase();

    if (order.payments && order.payments.length > 0) {
      return order.payments.reduce(
        (sum, p) => sum + Number(p.payment_amount),
        0
      );
    }

    if (status === "PARTIAL_PAID") {
      return Number(order.balance);
    }

    if (status === "PAID") {
      return Number(order.net_total);
    }

    return Number(order.balance);
  };

  const getCheckInPractitionerName = (checkIn: OrderExcelCheckIn): string => {
    if (checkIn.practitioners && checkIn.practitioners.length > 0) {
      const names = checkIn.practitioners
        .filter((p) => p.practitioner?.name)
        .map((p) => p.practitioner!.name)
        .join(", ");
      if (names) return names;
    }
    return checkIn.practitioner?.name || "";
  };

  const getOrderItemPractitioner = (
    order: OrderExcel,
    item: OrderItemExcel,
    usage: Map<string, number>
  ): string => {
    if (!order.checkins?.length || !item.service_id) return "";

    const matchingCheckins = order.checkins.filter(
      (c) => c.service_id === item.service_id
    );
    if (matchingCheckins.length === 0) return "";

    const usedCount = usage.get(item.service_id) || 0;
    const checkIn =
      matchingCheckins[usedCount] ?? matchingCheckins[matchingCheckins.length - 1];

    usage.set(item.service_id, usedCount + 1);

    return getCheckInPractitionerName(checkIn);
  };

  const columns: any[] = [
    {
      title: "Date",
      dataIndex: "created_at",
      key: "created_at",
      editable: false,
      width: 170,
      ellipsis: true,
      sorter: (a: Order, b: Order) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      render: (v: Order, r: Order) => {
        return format(new Date(r.created_at), "dd MMM,yyyy h:mm a");
      },
    },
    {
      title: "OrderNo",
      dataIndex: "order_id",
      key: "order_id",
      editable: false,
      width: 110,
      ellipsis: true,
      sorter: (a: Order, b: Order) =>
        a.order_id.toLowerCase().localeCompare(b.order_id.toLowerCase()),
      render: (v: number, r: Order) => {
        return <Link to={paths.getSaleDetailRoute(r.id)}>{r.order_id}</Link>;
      },
    },
    {
      title: "Member",
      dataIndex: "member",
      key: "member",
      editable: false,
      width: 135,
      sorter: (a: Order, b: Order) =>
        a.member.name.toLowerCase().localeCompare(b.member.name.toLowerCase()),
      render: (v: number, r: Order) => {
        const val =
          r.member.clinic_members.length > 0
            ? r.member.clinic_members[0].name
            : r.member.name;
        return val;
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 90,
      ellipsis: true,
      sorter: (a: Order, b: Order) => a.status.localeCompare(b.status),
      editable: false,
    },
    {
      title: "Total Amount",
      dataIndex: "net_total",
      key: "net_total",
      editable: true,
      width: 134,
      sorter: (a: Order, b: Order) => Number(a.net_total) - Number(b.net_total),
      render: (v: number, r: Order) => {
        return `${formatMoney(Number(r.net_total))} ${getCurrency(props.currentClinic)}`;
      },
    },
    {
      title: "Payment Made",
      dataIndex: "balance",
      key: "balance",
      editable: true,
      width: 143,
      sorter: (a: Order, b: Order) =>
        getPaymentMadeAmount(a) - getPaymentMadeAmount(b),
      render: (v: number, r: Order) => {
        return `${formatMoney(getPaymentMadeAmount(r))} ${getCurrency(props.currentClinic)}`;
      },
    },
    {
      title: "Balance Due",
      dataIndex: "credit_balance",
      key: "credit_balance",
      width: 126,
      sorter: (a: Order, b: Order) =>
        Number(a.credit_balance) - Number(b.credit_balance),
      editable: true,
      render: (v: number, r: Order) => {
        return `${formatMoney(Number(r.credit_balance))} ${getCurrency(props.currentClinic)}`;
      },
    },
    {
      title: "Payment Status",
      dataIndex: "payment_status",
      key: "payment_status",
      width: 145,
      ellipsis: true,
      sorter: (a: Order, b: Order) =>
        a.payment_method.localeCompare(b.payment_status),
      editable: true,
    },
    {
      title: "Payment",
      dataIndex: "payment_method",
      key: "payment_method",
      width: 103,
      ellipsis: true,
      sorter: (a: Order, b: Order) =>
        a.payment_method.localeCompare(b.payment_method),
      editable: true,
    },
    {
      title: "Seller",
      dataIndex: "seller",
      key: "seller",
      editable: false,
      width: 103,
      sorter: (a: Order, b: Order) =>
        a.seller?.display_name
          ?.toLowerCase()
          .localeCompare(b.seller?.display_name?.toLowerCase() ?? ""),
      render: (v: number, r: Order) => {
        return r.seller?.display_name;
      },
    },
    {
      title: "SoldBy",
      dataIndex: "user",
      key: "user",
      editable: false,
      width: 125,
      sorter: (a: Order, b: Order) =>
        a.user.name.toLowerCase().localeCompare(b.user.name.toLowerCase()),
      render: (v: number, r: Order) => {
        return r.user.name;
      },
    },
    {
      title: "Note",
      dataIndex: "metadata",
      key: "metadata",
      editable: false,
      width: 170,
      render: (v: number, r: Order) => {
        if (!r.metadata) return null;
        const metadata = JSON.parse(r.metadata);
        return (
          <Typography.Paragraph ellipsis={{ rows: 2, expandable: true }}>
            {metadata?.merchant_note}
          </Typography.Paragraph>
        );
      },
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      editable: false,
      fixed: "right",
      width: 74,
      render: (v: number, r: Order) => {
        return (
          <a
            rel="noreferrer"
            target="_blank"
            href={`https://greattime-api-core-hs6rtohe3q-uc.a.run.app/api/invoice?orderId=${r.id}&template=${template.template}&color=${template.color}`}
          >
            <PdfExportIcon />
          </a>
        );
      },
    },
    // https://greattime-api-core-hs6rtohe3q-uc.a.run.app/api/invoice?orderId=clp0tt75c0003s601qvv52bfr&template=template1
    // {
    //     title: 'Action',
    //     key: 'action',
    //     align: 'center',
    //     width: '10%',
    //     dataIndex: 'action',
    //     editable: false,
    //     fixed: 'right',
    //     render: (v: number, record: Account) => {
    //        // const editable = isEditing(record);
    //         return (
    //             <ButtonGroup>
    //                 {editable ?
    //                     (
    //                         saveLoading ? <Button type="primary" icon={<LoadingOutlined />} >saving...</Button> :
    //                             < Popconfirm placement="topRight" title={`Sure to save ${record.account_number}?`} onConfirm={() => handleSave(record)} onCancel={() => setEditingKey('')}>
    //                                 <Button type='primary' icon={<SaveOutlined />} >save</Button>
    //                             </Popconfirm>
    //                     )
    //                     :
    //                     <Button shape="circle" icon={<EditOutlined />}
    //                         onClick={() => edit(record)}
    //                     />
    //                 }
    //             </ButtonGroup >
    //         );

    //     }
    // }
  ];
  const control = (
    <Row style={{ marginTop: -18 }}>
      <Col flex={4}>
        <Row>
          <Col span={8}>Sales</Col>
          <Col span={8} offset={8}>
            <Search
              defaultValue={searchText || undefined}
              placeholder={`search ${totalCount.data?.aggregateOrder._count.id}...`}
              allowClear
              size="middle"
              onSearch={(val: any) => setSearchText(val)}
            />
          </Col>
        </Row>
      </Col>
      <Col flex={0}>
        <DateRange
          startDate={ranges.startDate}
          endDate={ranges.endDate}
          onSelect={onDateChange}
        />
      </Col>
    </Row>
  );
  const context = (
    <Row style={{ marginTop: 8 }}>
      <Col span={24}>
        <Form form={form} component={false}>
          <HorizontalScrollContainer>
            <Table
              showHeader={data.orders.length > 0}
              dataSource={data.orders.map((b) => ({ key: b.id, ...b }))}
              columns={columns}
              rowClassName="editable-row"
              scroll={{ x: 1650 }}
              onChange={(p: PaginationConfig) => {
                setPagination(p);
              }}
              pagination={{
                total: totalCount.data?.aggregateOrder._count.id || 0,
                current: pagination?.current,
                pageSize: pagination?.pageSize,
                defaultPageSize: 20,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "30", "50"],
              }}
            />
          </HorizontalScrollContainer>
        </Form>
      </Col>
    </Row>
  );
  const filters = (
    <Row style={{ marginTop: 4, marginRight: 2 }}>
      <Col span={16}>
        <MultiSelect
          selected={filter}
          placeholder="Select orders status"
          options={["ACTIVE", "INACTIVE", "CANCEL", "DONE"]}
          onChange={setFilter}
        />
      </Col>
      <Col span={6}>
        <MultiSelect
          selected={paymentFilter}
          placeholder="Select orders Payment Status"
          options={["PAID", "UNPAID", "PARTIAL_PAID"]}
          onChange={SetPaymentFilter}
        />
      </Col>
      <Col span={2}>
        <Button
          size="small"
          loading={exporting}
          icon={<FileExcelFilled />}
          onClick={handleExport}
        >
          Export
        </Button>
      </Col>
    </Row>
  );

  return (
    <React.Fragment>
      {control}
      {filters}
      {context}
    </React.Fragment>
  );
};

export default withClinic(SalePage);
